/**
 * FreeDailyPro — Client-side matcher
 *
 * Scores every tool in the catalog against a query and returns the top N candidates.
 * Pure synchronous JS — no network, no AI.
 *
 * Exports:
 *   topCandidates(query, catalog, n)  → top N scored tools
 *   parseWorkflow(query, catalog)     → ordered tool IDs for multi-step queries
 */

'use strict';

// ── Stopwords ────────────────────────────────────────────────────────────────
const STOPWORDS = new Set([
  'a','an','the','and','then','or','to','of','for','with','my','please',
  'can','you','how','do','i','me','it','is','are','on','in','at','want',
  'need','help','make','get','use','using','into','from','some','all',
  'this','that','what','where','when','will','would','could','should',
]);

// ── Synonym map ──────────────────────────────────────────────────────────────
// key → array of synonyms (all lowercase, stemmed loosely)
const SYNONYMS = {
  // compression / size
  compress:   ['compress','shrink','reduce','smaller','minimize','optimize','lighter','size'],
  // merging
  merge:      ['merge','combine','join','concatenate','together','unite','assemble'],
  // splitting
  split:      ['split','separate','divide','break','extract','cut','isolate'],
  // conversion
  convert:    ['convert','change','transform','export','save as','format'],
  // editing
  edit:       ['edit','modify','change','update','alter','annotate'],
  // creation
  create:     ['create','make','build','generate','produce','design'],
  // removal
  remove:     ['remove','delete','strip','clean','erase','eliminate'],
  // protection
  protect:    ['protect','secure','lock','password','encrypt','hide','redact'],
  // rotation
  rotate:     ['rotate','flip','turn','orientation'],
  // resize
  resize:     ['resize','scale','crop','dimensions','width','height'],
  // download / extract
  extract:    ['extract','get','pull','download','grab','rip'],
  // text reading
  read:       ['read','scan','ocr','recognize','digitize','extract text'],
  // budget / money
  budget:     ['budget','money','finance','spending','cost','track','expense'],
  // calculate
  calculate:  ['calculate','compute','estimate','figure out','find out'],
  // plan
  plan:       ['plan','organize','schedule','arrange','prepare','checklist'],
  // sign
  sign:       ['sign','signature','esign','approve','authorize'],
  // check
  check:      ['check','verify','validate','test','inspect','analyze'],
  // audio specific
  audio:      ['audio','sound','music','mp3','wav','voice','recording'],
  // video specific
  video:      ['video','movie','clip','mp4','film','footage'],
  // image specific
  image:      ['image','photo','picture','photo','img','jpg','png','graphic'],
  // pdf specific
  pdf:        ['pdf','document','doc','file','acrobat'],
  // tax
  tax:        ['tax','irs','deduction','fiscal','levy','return'],
  // qr
  qr:         ['qr','barcode','code','scan'],
  // travel
  travel:     ['travel','trip','vacation','holiday','tour'],
};

// Build reverse map: synonym → canonical key
const SYNONYM_LOOKUP = new Map();
for (const [canonical, terms] of Object.entries(SYNONYMS)) {
  for (const t of terms) SYNONYM_LOOKUP.set(t, canonical);
}

// ── Tokenize ─────────────────────────────────────────────────────────────────
function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOPWORDS.has(t));
}

// Map token to its canonical synonym (or itself)
function canonical(token) {
  return SYNONYM_LOOKUP.get(token) ?? token;
}

// ── Score a single tool against query tokens ─────────────────────────────────
function scoreTool(tool, queryTokens, queryCanonicals) {
  const nameTokens  = tokenize(tool.name);
  const kwTokens    = (tool.keywords || []).flatMap(k => tokenize(k));
  const descTokens  = tokenize(tool.description || '');

  const nameCans  = nameTokens.map(canonical);
  const kwCans    = kwTokens.map(canonical);
  const descCans  = descTokens.map(canonical);

  let score = 0;
  let matched = 0;

  for (const qc of queryCanonicals) {
    // Exact canonical match in name (highest weight)
    if (nameCans.includes(qc))  { score += 10; matched++; continue; }
    // Exact canonical match in keywords
    if (kwCans.includes(qc))    { score += 5;  matched++; continue; }
    // Exact canonical match in description
    if (descCans.includes(qc))  { score += 2;  matched++; continue; }
    // Partial / prefix match in name
    if (nameCans.some(n => n.startsWith(qc) || qc.startsWith(n))) { score += 4; matched++; continue; }
    // Partial / prefix match in keywords
    if (kwCans.some(k => k.startsWith(qc) || qc.startsWith(k)))   { score += 2; matched++; }
  }

  // Coverage bonus: reward tools that match most of the meaningful query tokens
  const coverage = queryCanonicals.length > 0 ? matched / queryCanonicals.length : 0;
  score *= (0.5 + 0.5 * coverage);

  // Category bonus: slight boost for category match
  if (queryCanonicals.some(qc => qc === tool.category)) score += 3;

  return score;
}

// ── Public: topCandidates ────────────────────────────────────────────────────
/**
 * Score all tools in catalog against query and return top N.
 * @param {string} query        Raw user query
 * @param {object[]} catalog    Full tool catalog array
 * @param {number} n            How many to return (default 5)
 * @returns {object[]}          Top N tools sorted by score desc, each with .score
 */
export function topCandidates(query, catalog, n = 5) {
  const tokens     = tokenize(query);
  const canonicals = tokens.map(canonical);

  if (canonicals.length === 0) return [];

  const scored = catalog
    .map(tool => ({ ...tool, score: scoreTool(tool, tokens, canonicals) }))
    .filter(t => t.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, n);
}

// ── Public: parseWorkflow ────────────────────────────────────────────────────
/**
 * Detect multi-tool intent in a query and return ordered tool IDs.
 * Triggers when the query contains "and", "then", "after", "before",
 * or a comma separating distinct tool intents.
 *
 * @param {string} query
 * @param {object[]} catalog
 * @returns {{ detected: boolean, toolIds: string[] }}
 */
export function parseWorkflow(query, catalog) {
  // Split query on connectors
  const CONNECTORS = /\b(and then|and|then|after|before|followed by|,)\b/i;
  const segments = query.split(CONNECTORS)
    .map(s => s.trim())
    .filter(s => s.length > 2 && !CONNECTORS.test(s));

  if (segments.length < 2) return { detected: false, toolIds: [] };

  // Score each segment independently against catalog
  const MIN_SCORE = 3;
  const toolIds = [];
  const seen = new Set();

  for (const seg of segments) {
    const top = topCandidates(seg, catalog, 1);
    if (top.length > 0 && top[0].score >= MIN_SCORE && !seen.has(top[0].id)) {
      toolIds.push(top[0].id);
      seen.add(top[0].id);
    }
  }

  if (toolIds.length < 2) return { detected: false, toolIds: [] };
  return { detected: true, toolIds };
}
